import { type ReactNode, createContext, useContext, useEffect } from 'react';

import type { FormInstance } from '@arco-design/web-react';

import {
  ResourceSectionReadOnlyProvider as SceneResourceSectionReadOnlyProvider,
  useResourceSectionReadOnly as useSceneResourceSectionReadOnly,
} from '../scene-layout/ResourceModuleReadOnlyGuard';
import type { ResourceEditMode } from '../scene-layout/types';
import { registerResourceFormContext } from './formContextRegistry';

export interface ResourceFormContextValue {
  deployTypeCode?: string;
  architecture?: string;
  calculatePriceData?: (
    formValues: Record<string, unknown>,
    innerForm: FormInstance,
    options?: { force?: boolean },
  ) => void | Promise<void>;
  presetImageLocked?: boolean;
  /** 下单：镜像有数据时隐藏删/改 */
  lockActionsWhenFilled?: boolean;
  skuType?: string;
  paymentTiming?: string;
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  skuId?: string;
  skuModuleSelectionEnabled?: boolean;
  resourceEditMode?: ResourceEditMode;
  /** 表单 data 层快照（无 versioned envelope） */
  presetConfigSnapshot?: Record<string, unknown>;
  /** 下单：SKU preset 勾选模块，锁定 = 直建锁定 ∪ 此处为 true 的模块 */
  presetSkuResourceModules?: Record<string, boolean>;
}

export function useResourceSectionReadOnly(): boolean {
  return useSceneResourceSectionReadOnly();
}

export function ResourceSectionReadOnlyProvider({ readOnly, children }: { readOnly: boolean; children: ReactNode }) {
  return <SceneResourceSectionReadOnlyProvider readOnly={readOnly}>{children}</SceneResourceSectionReadOnlyProvider>;
}

/** 将 ResourceFormContext 绑到 FormInstance，供 linkage effect 读取下单模式 */
export function BindResourceFormContextToForm({ form, value }: { form: FormInstance; value: ResourceFormContextValue }) {
  useEffect(() => {
    registerResourceFormContext(form, value);
    return () => registerResourceFormContext(form, null);
  }, [form, value]);
  return null;
}

const ResourceFormContext = createContext<ResourceFormContextValue | null>(null);

export function ResourceFormProvider({ value, children }: { value: ResourceFormContextValue; children: ReactNode }) {
  return <ResourceFormContext.Provider value={value}>{children}</ResourceFormContext.Provider>;
}

export function useResourceFormContext(): ResourceFormContextValue {
  const context = useContext(ResourceFormContext);
  if (!context) {
    throw new Error('useResourceFormContext must be used within ResourceFormProvider');
  }
  return context;
}

export function useOptionalResourceFormContext(): ResourceFormContextValue | null {
  return useContext(ResourceFormContext);
}
